import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, ArrowRight, Layers } from 'lucide-react';
import { CardSectionProps } from '../../../types/compositeCards';
import { PrimeArtifact } from '../../../types/primeArtifact';
import styles from './PrimeArtifactCard.module.css';

interface PrimeArtifactCardProps extends CardSectionProps {}

export const PrimeArtifactCard: React.FC<PrimeArtifactCardProps> = ({ card, onAction }) => {
  const navigate = useNavigate();

  // Extract prime artifact data
  const data = card.data as any;
  const primeArtifact: PrimeArtifact = data.primeArtifact || data.artifact;

  if (!primeArtifact) {
    return <div>No prime artifact data available</div>;
  }

  const developmentStatus = primeArtifact.development_status || 'IDENTIFIED';
  const isTemplateReady = primeArtifact.is_template_ready || false;
  
  const approvalStatus = {
    details: primeArtifact.details_approved || false,
    states: primeArtifact.states_approved || false,
    transitions: primeArtifact.transitions_approved || false,
    processes: primeArtifact.processes_approved || false,
  };

  const handleOpenInManager = () => {
    navigate(`/prime-model/${primeArtifact.id}`, { 
      state: { artifact: primeArtifact } 
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'IDENTIFIED': return '#6b7280';
      case 'DEFINED': return '#3b82f6';
      case 'IMPLEMENTED': return '#8b5cf6';
      case 'VALIDATED': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'IDENTIFIED': return <Clock size={16} />;
      case 'DEFINED': return <Layers size={16} />;
      case 'IMPLEMENTED': return <CheckCircle size={16} />;
      case 'VALIDATED': return <CheckCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const statusSteps = ['IDENTIFIED', 'DEFINED', 'IMPLEMENTED', 'VALIDATED'];
  const currentStepIndex = statusSteps.indexOf(developmentStatus);

  return (
    <div className={styles.primeArtifactCard}>
      {/* Header */}
      <div className={styles.primeArtifactCardHeader}>
        <div className={styles.primeArtifactCardTitle}>
          <h3>{primeArtifact.name}</h3>
          {primeArtifact.artifact_type_name && (
            <span className={styles.artifactTypeBadge}>{primeArtifact.artifact_type_name}</span>
          )}
        </div>
        {isTemplateReady && (
          <div className={styles.templateReadyBadge}>
            <CheckCircle size={14} />
            Template Ready
          </div>
        )}
      </div>

      {/* Description */}
      {primeArtifact.description && (
        <p className={styles.primeArtifactDescription}>{primeArtifact.description}</p>
      )}

      {/* Development Status Workflow */}
      <div className={styles.developmentStatusSection}>
        <h4>Development Status</h4>
        <div className={styles.statusWorkflow}>
          {statusSteps.map((step, index) => (
            <React.Fragment key={step}>
              <div 
                className={`${styles.statusStep} ${index <= currentStepIndex ? styles.active : styles.inactive}`}
                style={{ 
                  borderColor: index <= currentStepIndex ? getStatusColor(step) : '#d1d5db',
                  color: index <= currentStepIndex ? getStatusColor(step) : '#9ca3af'
                }}
              >
                {index <= currentStepIndex ? getStatusIcon(step) : <div className={styles.statusDot} />}
                <span>{step}</span>
              </div>
              {index < statusSteps.length - 1 && (
                <div className={`${styles.statusConnector} ${index < currentStepIndex ? styles.active : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Tab Approval Status */}
      <div className={styles.approvalStatusSection}>
        <h4>Tab Approvals</h4>
        <div className={styles.approvalGrid}>
          <div className={styles.approvalItem}>
            {approvalStatus.details ? (
              <CheckCircle size={16} className={styles.approved} />
            ) : (
              <XCircle size={16} className={styles.notApproved} />
            )}
            <span>Details</span>
          </div>
          <div className={styles.approvalItem}>
            {approvalStatus.states ? (
              <CheckCircle size={16} className={styles.approved} />
            ) : (
              <XCircle size={16} className={styles.notApproved} />
            )}
            <span>States</span>
          </div>
          <div className={styles.approvalItem}>
            {approvalStatus.transitions ? (
              <CheckCircle size={16} className={styles.approved} />
            ) : (
              <XCircle size={16} className={styles.notApproved} />
            )}
            <span>Transitions</span>
          </div>
          <div className={styles.approvalItem}>
            {approvalStatus.processes ? (
              <CheckCircle size={16} className={styles.approved} />
            ) : (
              <XCircle size={16} className={styles.notApproved} />
            )}
            <span>Processes</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className={styles.primeArtifactCardActions}>
        <button 
          className={styles.openManagerButton}
          onClick={handleOpenInManager}
        >
          Open in Manager
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

